import React, { useState, useEffect, useCallback } from 'react';

/**
 * Bluetooth Test Page
 *
 * Uses Web Bluetooth API to discover and connect to nearby devices.
 * Note: Browser security requires user interaction to scan - a picker dialog appears.
 */

interface BluetoothDeviceInfo {
  id: string;
  name: string | undefined;
  connected: boolean;
  gatt?: BluetoothRemoteGATTServer;
  device: BluetoothDevice;
  discoveredAt: Date;
  services?: string[];
  rssi?: number;
}

const BluetoothTest: React.FC = () => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [devices, setDevices] = useState<BluetoothDeviceInfo[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<BluetoothDeviceInfo | null>(null);
  const [deviceDetails, setDeviceDetails] = useState<Record<string, string>>({});

  // Check Web Bluetooth support
  useEffect(() => {
    const supported = 'bluetooth' in navigator;
    setIsSupported(supported);

    if (!supported) {
      setError('Web Bluetooth is not supported in this browser. Try Chrome on desktop or Android.');
    }
  }, []);

  // Handle device disconnection
  const handleDisconnect = useCallback((deviceId: string) => {
    setDevices(prev => prev.map(d =>
      d.id === deviceId ? { ...d, connected: false } : d
    ));
    if (selectedDevice?.id === deviceId) {
      setSelectedDevice(prev => prev ? { ...prev, connected: false } : null);
    }
  }, [selectedDevice]);

  // Scan for devices
  const scanForDevices = async () => {
    if (!isSupported) return;

    setIsScanning(true);
    setError(null);

    try {
      // Request device - browser will show picker with all available devices
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          'battery_service',
          'device_information',
          'generic_access',
          'generic_attribute',
        ]
      });

      // Add disconnect listener
      device.addEventListener('gattserverdisconnected', () => {
        handleDisconnect(device.id);
      });

      // Check if device already in list
      const existingIndex = devices.findIndex(d => d.id === device.id);

      const deviceInfo: BluetoothDeviceInfo = {
        id: device.id,
        name: device.name,
        connected: false,
        device: device,
        discoveredAt: new Date(),
      };

      if (existingIndex >= 0) {
        // Update existing device
        setDevices(prev => {
          const updated = [...prev];
          updated[existingIndex] = deviceInfo;
          return updated;
        });
      } else {
        // Add new device
        setDevices(prev => [deviceInfo, ...prev]);
      }

    } catch (err: any) {
      if (err.name === 'NotFoundError') {
        // User cancelled the picker
        setError('No device selected. Click "Scan" to try again.');
      } else {
        setError(err.message || 'Failed to scan for devices');
      }
    } finally {
      setIsScanning(false);
    }
  };

  // Connect to a device
  const connectToDevice = async (deviceInfo: BluetoothDeviceInfo) => {
    setError(null);

    try {
      if (!deviceInfo.device.gatt) {
        setError('Device does not support GATT');
        return;
      }

      const server = await deviceInfo.device.gatt.connect();

      // Update device info
      const updatedDevice: BluetoothDeviceInfo = {
        ...deviceInfo,
        connected: true,
        gatt: server,
      };

      setDevices(prev => prev.map(d =>
        d.id === deviceInfo.id ? updatedDevice : d
      ));

      setSelectedDevice(updatedDevice);

      // Try to read device information
      await readDeviceInfo(updatedDevice);

    } catch (err: any) {
      setError(`Failed to connect: ${err.message}`);
    }
  };

  // Disconnect from a device
  const disconnectDevice = async (deviceInfo: BluetoothDeviceInfo) => {
    try {
      if (deviceInfo.gatt?.connected) {
        deviceInfo.gatt.disconnect();
      }

      setDevices(prev => prev.map(d =>
        d.id === deviceInfo.id ? { ...d, connected: false, gatt: undefined } : d
      ));

      if (selectedDevice?.id === deviceInfo.id) {
        setSelectedDevice(null);
        setDeviceDetails({});
      }
    } catch (err: any) {
      setError(`Failed to disconnect: ${err.message}`);
    }
  };

  // Read device information
  const readDeviceInfo = async (deviceInfo: BluetoothDeviceInfo) => {
    if (!deviceInfo.gatt?.connected) return;

    const details: Record<string, string> = {};

    try {
      // Try to get Device Information service
      const service = await deviceInfo.gatt.getPrimaryService('device_information');

      // Read available characteristics
      const characteristics = [
        { uuid: 'manufacturer_name_string', label: 'Manufacturer' },
        { uuid: 'model_number_string', label: 'Model' },
        { uuid: 'serial_number_string', label: 'Serial Number' },
        { uuid: 'firmware_revision_string', label: 'Firmware' },
        { uuid: 'hardware_revision_string', label: 'Hardware' },
        { uuid: 'software_revision_string', label: 'Software' },
      ];

      for (const char of characteristics) {
        try {
          const characteristic = await service.getCharacteristic(char.uuid);
          const value = await characteristic.readValue();
          details[char.label] = new TextDecoder().decode(value);
        } catch {
          // Characteristic not available
        }
      }
    } catch {
      // Device Information service not available
    }

    try {
      // Try to get battery level
      const batteryService = await deviceInfo.gatt.getPrimaryService('battery_service');
      const batteryChar = await batteryService.getCharacteristic('battery_level');
      const batteryValue = await batteryChar.readValue();
      details['Battery'] = `${batteryValue.getUint8(0)}%`;
    } catch {
      // Battery service not available
    }

    // Try to list all services
    try {
      const services = await deviceInfo.gatt.getPrimaryServices();
      details['Services'] = services.map(s => s.uuid).join(', ') || 'None found';
    } catch {
      // Can't enumerate services
    }

    setDeviceDetails(details);
  };

  // Remove device from list
  const removeDevice = (deviceId: string) => {
    const device = devices.find(d => d.id === deviceId);
    if (device?.gatt?.connected) {
      device.gatt.disconnect();
    }
    setDevices(prev => prev.filter(d => d.id !== deviceId));
    if (selectedDevice?.id === deviceId) {
      setSelectedDevice(null);
      setDeviceDetails({});
    }
  };

  // Clear all devices
  const clearAllDevices = () => {
    devices.forEach(d => {
      if (d.gatt?.connected) {
        d.gatt.disconnect();
      }
    });
    setDevices([]);
    setSelectedDevice(null);
    setDeviceDetails({});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Bluetooth Device Scanner
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Discover and connect to nearby Bluetooth devices
        </p>
      </div>

      {/* Status & Instructions */}
      {!isSupported ? (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h2 className="font-semibold text-red-800 dark:text-red-200 mb-2">Not Supported</h2>
          <p className="text-red-700 dark:text-red-300 text-sm">
            Web Bluetooth is not available in this browser. Please use:
          </p>
          <ul className="list-disc list-inside text-red-700 dark:text-red-300 text-sm mt-2">
            <li>Chrome on Windows, macOS, or Linux</li>
            <li>Chrome on Android</li>
            <li>Edge on Windows</li>
          </ul>
          <p className="text-red-700 dark:text-red-300 text-sm mt-2">
            <strong>Note:</strong> Safari and iOS browsers do not support Web Bluetooth.
          </p>
        </div>
      ) : (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h2 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">How it works:</h2>
          <ol className="list-decimal list-inside text-blue-700 dark:text-blue-300 text-sm space-y-1">
            <li>Click "Scan for Devices" - browser will show a device picker</li>
            <li>Select a device from the picker to add it to the list</li>
            <li>Click "Connect" to establish a connection</li>
            <li>View device details once connected</li>
          </ol>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200 text-sm">{error}</p>
        </div>
      )}

      {/* Scan Button */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center gap-4">
          <button
            onClick={scanForDevices}
            disabled={!isSupported || isScanning}
            className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isSupported && !isScanning
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {isScanning ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Scanning...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
                Scan for Devices
              </>
            )}
          </button>

          {devices.length > 0 && (
            <button
              onClick={clearAllDevices}
              className="px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Device List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Discovered Devices ({devices.length})
        </h2>

        {devices.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
            <p>No devices discovered yet.</p>
            <p className="text-sm mt-1">Click "Scan for Devices" to find nearby Bluetooth devices.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {devices.map((device) => (
              <div
                key={device.id}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  selectedDevice?.id === device.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Connection status indicator */}
                    <div className={`w-3 h-3 rounded-full ${
                      device.connected ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'
                    }`}></div>

                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {device.name || 'Unknown Device'}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        ID: {device.id.slice(0, 20)}...
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Found: {device.discoveredAt.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {device.connected ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedDevice(device);
                            readDeviceInfo(device);
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => disconnectDevice(device)}
                          className="px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800"
                        >
                          Disconnect
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => connectToDevice(device)}
                        className="px-3 py-1.5 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800"
                      >
                        Connect
                      </button>
                    )}
                    <button
                      onClick={() => removeDevice(device.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                      title="Remove"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Device Details */}
      {selectedDevice && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Device Details: {selectedDevice.name || 'Unknown'}
          </h2>

          {Object.keys(deviceDetails).length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {selectedDevice.connected
                ? 'Reading device information...'
                : 'Connect to device to view details'}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(deviceDetails).map(([key, value]) => (
                <div key={key} className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                  <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase">{key}</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white mt-1 break-all">
                    {value}
                  </dd>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Card */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">About Web Bluetooth:</h3>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• Browser shows a picker dialog - you select which device to discover</li>
          <li>• Cannot silently scan all nearby devices (browser security)</li>
          <li>• Works with Bluetooth Low Energy (BLE) devices</li>
          <li>• Classic Bluetooth (like audio devices) may not be fully supported</li>
          <li>• Requires HTTPS (or localhost for development)</li>
        </ul>
      </div>
    </div>
  );
};

export default BluetoothTest;
